function setupSirePurchasePageOverlays() {
  const isSirePurchasePage =
    document.body?.classList.contains('sire-mobile-purchase-optimized') ||
    document.body?.classList.contains('sire-conversion-landing');

  if (!isSirePurchasePage) return;

  let previewToolbarObserver;

  const updatePreviewToolbarOffset = () => {
    const toolbar = document.getElementById('PBarNextFrameWrapper');
    const height = toolbar ? Math.ceil(toolbar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--sire-preview-toolbar-height', `${height}px`);

    if (toolbar && 'ResizeObserver' in window && !previewToolbarObserver) {
      previewToolbarObserver = new ResizeObserver(updatePreviewToolbarOffset);
      previewToolbarObserver.observe(toolbar);
    }
  };

  const closeKlaviyoPopup = () => {
    const dialog = document.querySelector(
      '[role="dialog"][aria-label="POPUP Form"][data-kl-scroll-locking-modal="true"]'
    );
    const closeButton = dialog?.querySelector('.klaviyo-close-form, [aria-label="Close dialog"]');
    if (!dialog) return;

    if (!dialog.dataset.sireDismissed && closeButton) {
      dialog.dataset.sireDismissed = 'true';
      closeButton.click();
    }

    dialog.setAttribute('aria-hidden', 'true');
    dialog.style.setProperty('display', 'none', 'important');
    document.body.classList.remove('klaviyo-prevent-body-scrolling');
  };

  const syncOverlays = () => {
    updatePreviewToolbarOffset();
    closeKlaviyoPopup();
  };

  syncOverlays();
  new MutationObserver(syncOverlays).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', updatePreviewToolbarOffset, { passive: true });
}

setupSirePurchasePageOverlays();

if (!customElements.get('sire-buy-box')) {
  customElements.define(
    'sire-buy-box',
    class SireBuyBox extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.form = document.getElementById(this.dataset.productFormId);
        this.productInfo = this.closest('product-info') || document;
        this.sellingPlanInput = this.querySelector('.sire-buybox__selling-plan');
        this.options = this.querySelectorAll('input[type="radio"][name^="sire_purchase_option_"]');
        this.mobileBar = document.querySelector(
          `[data-sire-mobile-bar][data-product-form-id="${this.dataset.productFormId}"]`
        );
        this.mobileButton = this.mobileBar?.querySelector('[data-sire-mobile-submit]');
        this.submitButton = this.form?.querySelector('[type="submit"][name="add"]');
        this.hasEngagedBuyBox = false;

        this.options.forEach((option) => {
          option.addEventListener('change', () => {
            this.hasEngagedBuyBox = true;
            this.applyOption(option);
          }, { signal });
        });

        this.addEventListener('focusin', () => this.engageBuyBox(), { signal });
        this.addEventListener('pointerdown', () => this.engageBuyBox(), { signal });

        this.mobileButton?.addEventListener('click', () => this.handleMobileClick(), { signal });

        this.form?.addEventListener('submit', () => {
          const selected = this.getSelectedOption();
          if (selected) this.syncForm(selected);
        }, { capture: true, signal });

        const checked = this.getSelectedOption();
        if (checked) this.applyOption(checked);
        this.setupVisibilityObservers();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.buyBoxObserver?.disconnect();
        this.submitObserver?.disconnect();
      }

      getSelectedOption() {
        return this.querySelector('input[type="radio"][name^="sire_purchase_option_"]:checked');
      }

      engageBuyBox() {
        if (this.hasEngagedBuyBox) return;
        this.hasEngagedBuyBox = true;
        this.updateMobileButton();
      }

      handleMobileClick() {
        if (!this.hasEngagedBuyBox) {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.engageBuyBox();
          return;
        }

        const selected = this.getSelectedOption();
        if (selected) this.syncForm(selected);
        if (this.submitButton && !this.submitButton.disabled && this.submitButton.getAttribute('aria-disabled') !== 'true') {
          this.submitButton.click();
        }
      }

      applyOption(option) {
        const variantOption = option.dataset.variantOption || '';
        if (variantOption) {
          const variantInput = Array.from(
            this.productInfo.querySelectorAll('variant-selects input[type="radio"]')
          ).find((input) => input.value === variantOption);
          if (variantInput && !variantInput.checked) variantInput.click();
        }

        this.classList.toggle('is-subscription', option.value === 'subscription');
        this.syncForm(option);
        this.updateMobileButton(option);
      }

      syncForm(option) {
        const sellingPlan = option.dataset.sellingPlan || '';
        const variantId = option.dataset.variantId || '';

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = sellingPlan;
          this.sellingPlanInput.disabled = sellingPlan === '';
        }

        if (variantId && this.form) {
          const variantIdInput = this.form.querySelector('input[name="id"]');
          if (variantIdInput) {
            variantIdInput.value = variantId;
            variantIdInput.disabled = false;
          }
        }

        if (this.submitButton && option.dataset.available) {
          const isAvailable = option.dataset.available === 'true';
          this.submitButton.disabled = !isAvailable;
          if (isAvailable) {
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
        }
      }

      updateMobileButton(option = this.getSelectedOption()) {
        if (!this.mobileButton) return;

        const scrollLabel = this.mobileButton.dataset.sireScrollLabel || 'Choose your protocol';
        const submitLabel = this.mobileButton.dataset.sireSubmitLabel || 'Add to cart';
        const price = option?.dataset.ctaPrice || '';
        const isAvailable = option?.dataset.available !== 'false';

        if (!this.hasEngagedBuyBox) {
          this.mobileButton.textContent = scrollLabel;
          this.mobileButton.disabled = false;
          return;
        }

        this.mobileButton.textContent = isAvailable
          ? `${submitLabel}${price ? ` — ${price}` : ''}`
          : 'Sold out';
        this.mobileButton.disabled = !isAvailable;
      }

      setupVisibilityObservers() {
        if (!this.mobileBar || !('IntersectionObserver' in window)) return;

        this.buyBoxObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.engageBuyBox();
        }, { threshold: 0.08 });
        this.buyBoxObserver.observe(this);

        if (this.submitButton) {
          this.submitObserver = new IntersectionObserver((entries) => {
            const normalButtonVisible = entries.some((entry) => entry.isIntersecting);
            this.mobileBar.classList.toggle('is-native-atc-visible', normalButtonVisible);
            this.mobileBar.setAttribute('aria-hidden', normalButtonVisible ? 'true' : 'false');
          }, { threshold: 0.35 });
          this.submitObserver.observe(this.submitButton);
        }
      }
    }
  );
}
